/** @jsx h */
import { Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { labels } from '../../config';
import { ClickRedirect } from '@3pc/click-redirect';
import { Tooltip } from '@3pc/tooltip';
import { Accordion } from '@3pc/accordion';

const TeaserMap = ({ result }) => {
  const accordionTarget = useRef(null);
  const accordionTargetInner = useRef(null);
  const accordionHandler = useRef(null);
  const clickRedirectEl = useRef(null);
  const clickRedirectTargetEl = useRef(null);

  const tooltipUrl = href => {
    const origin = window.location.origin;
    const url = `${
      href.includes('https://') || href.includes('http://') ? '' : origin
    }${href}`;

    return url;
  };

  // Click redirect
  useEffect(() => {
    const clickRedirect = clickRedirectEl.current;
    const clickRedirectTarget = clickRedirectTargetEl.current;

    if (!clickRedirect || !clickRedirectTarget) return;

    const clickRedirectInstance = new ClickRedirect(
      clickRedirect,
      clickRedirectTarget
    );
    clickRedirectInstance.activate();

    // return () => clickRedirectInstance?.deactivate();
  });

  // Tooltip
  useEffect(() => {
    if (!clickRedirectEl.current) return;

    const tooltipInstance = new Tooltip(clickRedirectEl.current, {
      contentAttribute: 'data-tooltip',
      tooltipIsHoverable: false,
      tooltipAlignLeft: true,
      tooltipHasTargetMaxWidth: true,
      targetIsFocusable: false,
    });
    tooltipInstance.init();

    return () => tooltipInstance?.destroy();
  });

  // Accordion
  useEffect(() => {
    const target = accordionTarget.current;
    const targetInner = accordionTargetInner.current;
    const handler = accordionHandler.current;

    if (!handler || !target || !targetInner) return;

    const accordionInstance = new Accordion({ target, targetInner, handler });
    accordionInstance.init();

    // return () => accordionInstance?.destroy();
  });

  return (
    <article
      className={`teaser-contact ${
        result.isHighlight ? 'teaser-contact--banderole' : ''
      }`}
    >
      <header className="teaser-contact__header">
        <div className="teaser-contact__info">
          <span
            className={`teaser-contact__icon icon icon--osm-map-pin ${
              result.isHighlight ? 'icon--osm-map-pin-highlight' : ''
            }`}
          >
            <svg className="icon__svg" role="img" aria-hidden="true">
              <use href="#icon-osm-map-pin" />
            </svg>
          </span>
          {(result.mark || result.mk) && (
            <div className="teaser-contact__meta">
              <div className="teaser-contact__type">
                {Array.isArray(result.mark || result.mk)
                  ? (result.mark || result.mk).join(
                      '<span className="teaser-contact__type-separator" aria-hidden="true">|</span>'
                    )
                  : result.mark || result.mk}
              </div>
            </div>
          )}
        </div>
        <h3
          className={`teaser-contact__heading${
            result.url || result.u ? ' click-redirect-source' : ''
          }`}
          data-click-redirect={result.url || result.u ? true : null}
          data-tooltip={
            result.url || result.u ? tooltipUrl(result.url || result.u) : null
          }
          ref={result.url || result.u ? clickRedirectEl : null}
        >
          <span className="sr-only">{labels.document.contact}</span>
          {result.title || result.tt}
        </h3>
      </header>
      <div className="teaser-contact__content">
        {(result.address ||
          result.phone ||
          result.email ||
          result.a ||
          result.p ||
          result.e) && (
          <div className="teaser-contact__address">
            <address className="address ">
              {((result.address &&
                (result.address?.street ||
                  result.address?.zip ||
                  result.address?.city)) ||
                (result.a &&
                  (result.a?.st || result.a?.zp || result.a?.ct))) && (
                <span className="address__office">
                  <span className="address__text">
                    {`${
                      result.address?.street || result.a?.st
                        ? result.address?.street || result.a?.st
                        : ''
                    }${
                      (result.address?.street || result.a?.st) &&
                      (result.address?.zip ||
                        result.address?.city ||
                        result.a?.zp ||
                        result.a?.ct)
                        ? ', '
                        : ' '
                    }${
                      result.address?.zip || result.a?.zp
                        ? result.address?.zip || result.a?.zp
                        : ''
                    }${
                      result.address?.street ||
                      result.address?.zip ||
                      result.a?.st ||
                      result.a?.zp
                        ? ' '
                        : ''
                    }${
                      result.address?.city || result.a?.ct
                        ? result.address?.city || result.a?.ct
                        : ''
                    }`}
                  </span>
                </span>
              )}
              {(result.phone ||
                result.email ||
                result.additionalPhones?.length ||
                result.p ||
                result.e ||
                result.pa?.length) && (
                <span className="address__contacts">
                  {(result.phone || result.p) && (
                    <Fragment>
                      <span className="address__heading">
                        {labels.document.phone}:
                      </span>
                      <span className="address__text">
                        <a
                          className="styled-link styled-link--tel address__link"
                          href={`tel:${result.phone || result.p}`}
                        >
                          {result.phone || result.p}
                        </a>
                        <span className="styled-link-replacement address__link">
                          {result.phone || result.p}
                        </span>
                      </span>
                    </Fragment>
                  )}
                  {(result.additionalPhones || result.pa) &&
                    (result.additionalPhones || result.pa).map(
                      (phone, index) => (
                        <Fragment key={`additional-phone-${index}`}>
                          <span className="address__heading">
                            {phone.label || phone.l}:
                          </span>
                          <span className="address__text">
                            <a
                              className="styled-link styled-link--tel address__link"
                              href={`tel:${phone.number || phone.n}`}
                            >
                              {phone.number || phone.n}
                            </a>
                            <span className="styled-link-replacement address__link">
                              {phone.number || phone.n}
                            </span>{' '}
                            {phone.text || phone.t}
                          </span>
                        </Fragment>
                      )
                    )}
                  {(result.email || result.e) && (
                    <Fragment>
                      <span className="address__heading">
                        {labels.document.email}:
                      </span>
                      <span className="address__text">
                        <a
                          className="styled-link styled-link--mailto address__link"
                          href={`mailto:${result.email || result.e}`}
                        >
                          {result.email || result.e}
                        </a>
                      </span>
                    </Fragment>
                  )}
                </span>
              )}
            </address>
          </div>
        )}
        {(result.addendum || result.ad) && (
          <div
            class="teaser-contact__addendum"
            dangerouslySetInnerHTML={{
              __html: result.addendum || result.ad,
            }}
          />
        )}
        {(result.openingHours || result.oh) && (
          <div className="accordion accordion--contact teaser-contact__schedules">
            <section className="accordion__item">
              <h4 className="accordion__heading">
                <button
                  type="button"
                  className="accordion__button"
                  ref={accordionHandler}
                >
                  {labels.document.openingHours}
                  <span className="accordion__icon icon icon--chevron-down-thick">
                    <svg className="icon__svg" role="img" aria-hidden="true">
                      <use href="#icon-chevron-down-thick" />
                    </svg>
                    <span className="sr-only">{labels.accordion.open}</span>
                  </span>
                  <span className="accordion__icon icon icon--chevron-up-thick">
                    <svg className="icon__svg" role="img" aria-hidden="true">
                      <use href="#icon-chevron-up-thick" />
                    </svg>
                    <span className="sr-only">{labels.accordion.close}</span>
                  </span>
                </button>
              </h4>
              <div className="accordion__content" ref={accordionTarget}>
                <div
                  className="accordion__content-inner"
                  ref={accordionTargetInner}
                  dangerouslySetInnerHTML={{
                    __html: result.openingHours || result.oh,
                  }}
                />
              </div>
            </section>
          </div>
        )}
      </div>
      {(result.url || result.urlLocalAssociations || result.u || result.ua) && (
        <footer className="teaser-contact__footer">
          {((result.url && !result.urlLocalAssociations) ||
            (result.u && !result.ua)) && (
            <a
              className={`styled-link teaser-contact__footer-link click-redirect-target ${
                result.isHighlight
                  ? 'styled-link--boxed-white'
                  : 'styled-link--boxed-blue'
              }`}
              href={result.url || result.u}
              ref={clickRedirectTargetEl}
            >
              <span className="styled-link__icon icon icon--arrow-internal">
                <svg className="icon__svg" role="img" aria-hidden="true">
                  <use href="#icon-arrow-internal" />
                </svg>
              </span>
              {result.linkText || result.lt}
              <span className="sr-only">{result.title || result.tt}</span>
            </a>
          )}
          {((result.urlLocalAssociations && !result.url) ||
            (result.ua && !result.u)) && (
            <a
              className={`styled-link teaser-contact__footer-link click-redirect-target ${
                result.isHighlight
                  ? 'styled-link--boxed-white'
                  : 'styled-link--boxed-blue'
              }`}
              href={result.urlLocalAssociations || result.ua}
              ref={clickRedirectTargetEl}
            >
              <span className="styled-link__icon icon icon--arrow-internal">
                <svg className="icon__svg" role="img" aria-hidden="true">
                  <use href="#icon-arrow-internal" />
                </svg>
              </span>
              {result.linkTextLocalAssociations || result.lta}
            </a>
          )}
          {((result.urlLocalAssociations && result.url) ||
            (result.ua && result.u)) && (
            <ul className="teaser-contact__footer-list">
              <li>
                <a
                  className={`styled-link teaser-contact__footer-link click-redirect-target ${
                    result.isHighlight
                      ? 'styled-link--boxed-white'
                      : 'styled-link--boxed-blue'
                  }`}
                  href={result.url || result.u}
                  ref={clickRedirectTargetEl}
                >
                  <span className="styled-link__icon icon icon--arrow-internal">
                    <svg className="icon__svg" role="img" aria-hidden="true">
                      <use href="#icon-arrow-internal" />
                    </svg>
                  </span>
                  {result.linkText || result.lt}
                  <span className="sr-only">{result.title || result.tt}</span>
                </a>
              </li>
              <li>
                <a
                  className={`styled-link teaser-contact__footer-link ${
                    result.isHighlight
                      ? 'styled-link--boxed-white'
                      : 'styled-link--boxed-blue'
                  }`}
                  href={result.urlLocalAssociations || result.ua}
                >
                  <span className="styled-link__icon icon icon--arrow-internal">
                    <svg className="icon__svg" role="img" aria-hidden="true">
                      <use href="#icon-arrow-internal" />
                    </svg>
                  </span>
                  {result.linkTextLocalAssociations || result.lta}
                </a>
              </li>
            </ul>
          )}
        </footer>
      )}
    </article>
  );
};

export default TeaserMap;
